// Shared utility functions for generating missing college data in Comparison Hub
// Reuses existing API endpoints from individual college strategy pages

import toast from 'react-hot-toast';
import { supabase } from '../../../../lib/supabase';
import { flattenCollegeData } from '../../../../utils/collegeDataFlattener';
import EssaySlotService from '../../../../services/EssaySlotService';

// Global queue to track ongoing generation requests
// This persists across component mounts/unmounts
const generationQueue = new Map();

/**
 * Check if a college is already being processed
 */
export const isGenerating = (collegeId, dataType) => {
  const key = `${collegeId}_${dataType}`;
  return generationQueue.has(key);
};

/**
 * Mark a college as being processed
 */
const startGeneration = (collegeId, dataType) => {
  const key = `${collegeId}_${dataType}`;
  generationQueue.set(key, { startTime: Date.now() });
};

/**
 * Mark a college as finished processing
 */
const endGeneration = (collegeId, dataType) => {
  const key = `${collegeId}_${dataType}`;
  generationQueue.delete(key);
};

/**
 * Detect what data is missing for a college
 * @param {Object} college - College object with related data
 * @returns {Array} - Array of missing data types
 */
export const getMissingData = (college) => {
  const missing = [];

  // Basic college data
  if (!college.global_colleges?.us_news_ranking && !college.global_colleges?.acceptance_rate) {
    missing.push('basic_data');
  }

  // Deadlines
  if (!college.early_deadline && !college.regular_deadline) {
    missing.push('deadlines');
  }

  // Essay requirements
  if (!college.essay_slots || college.essay_slots.length === 0) {
    missing.push('essays');
  }

  // Campus culture
  if (!college.global_colleges?.campus_culture && !college.global_colleges?.core_values) {
    missing.push('culture');
  }

  // Financial data
  if (!college.global_colleges?.avg_merit_aid && !college.global_colleges?.tuition) {
    missing.push('financial');
  }

  // Honors programs
  if (!college.honors_program_info) {
    missing.push('honors');
  }

  return missing;
};

/**
 * Generate basic college data (rankings, demographics, acceptance rates)
 * @param {Object} college - College object
 * @param {Function} onSuccess - Callback after successful generation
 */
export const generateBasicData = async (college, onSuccess) => {
  const collegeId = college.id || college.global_college_id;

  // Check if already generating
  if (isGenerating(collegeId, 'basic')) {
    console.log(`⏭️ Skipping ${college.name} - already generating basic data`);
    return;
  }

  startGeneration(collegeId, 'basic');

  try {
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-college-data',
        collegeName: college.global_colleges?.name || college.name,
        dataFields: 'all'
      })
    });

    if (!response.ok) throw new Error('Failed to fetch college data');

    const data = await response.json();
    toast.success(`Generated data for ${college.global_colleges?.name || college.name}`);

    if (onSuccess) onSuccess(data);
    return data;
  } catch (error) {
    console.error('Error generating basic data:', error);
    toast.error('Failed to generate college data');
    throw error;
  } finally {
    endGeneration(collegeId, 'basic');
  }
};

/**
 * Generate application deadlines
 * @param {Object} college - College object
 * @param {Function} onSuccess - Callback after successful generation
 */
export const generateDeadlines = async (college, onSuccess) => {
  try {
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-college-data',
        collegeName: college.global_colleges?.name || college.name,
        dataFields: 'deadlines'
      })
    });

    if (!response.ok) throw new Error('Failed to fetch deadlines');

    const data = await response.json();
    toast.success(`Generated deadlines for ${college.global_colleges?.name || college.name}`);

    if (onSuccess) onSuccess(data);
    return data;
  } catch (error) {
    console.error('Error generating deadlines:', error);
    toast.error('Failed to generate deadlines');
    throw error;
  }
};

/**
 * Generate essay requirements
 * @param {Object} college - College object
 * @param {string} userId - Current user ID
 * @param {Function} onSuccess - Callback after successful generation
 */
export const generateEssayRequirements = async (college, userId, onSuccess) => {
  try {
    const currentYear = new Date().getFullYear();
    const academicYear = new Date().getMonth() >= 9 ?
      `${currentYear}-${currentYear + 1}` :
      `${currentYear - 1}-${currentYear}`;

    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-essay-prompts',
        collegeName: college.global_colleges?.name || college.name,
        year: academicYear,
        userId: userId,
        collegeId: college.id
      })
    });

    if (!response.ok) throw new Error('Failed to fetch essay requirements');

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error(result.error || 'No essay data returned from AI service');
    }

    console.log('Essay prompts fetched successfully:', result);
    console.log('College object:', college);
    console.log('College ID:', college.id);
    console.log('Global college ID:', college.global_college_id || college.global_colleges?.id);

    // Step 1: Save essay_prompts to global_colleges
    const globalCollegeId = college.global_college_id || college.global_colleges?.id;

    if (!globalCollegeId) {
      console.error('❌ No global college ID found!', {
        college_id: college.id,
        global_college_id: college.global_college_id,
        global_colleges: college.global_colleges
      });
      throw new Error('No global college ID found - cannot save essay prompts');
    }

    console.log(`💾 Attempting to save essay prompts to global_colleges (id: ${globalCollegeId})`);
    console.log('Essay data to save:', result.data);

    const { data: updateData, error: updateError } = await supabase
      .from('global_colleges')
      .update({
        essay_prompts: result.data,
        data_last_updated: new Date().toISOString()
      })
      .eq('id', globalCollegeId)
      .select();

    if (updateError) {
      console.error('❌ Database update error:', updateError);
      throw new Error('Failed to save essay prompts: ' + updateError.message);
    }

    console.log(`✅ Saved essay prompts to global_colleges for ${college.global_colleges?.name || college.name}`);
    console.log('Updated rows:', updateData);

    // Step 2: Generate essay_slots from the saved prompts
    try {
      console.log(`🎯 Generating essay slots for college ${college.id}, user ${userId}`);
      console.log('Essay data being passed:', result.data);

      const slots = await EssaySlotService.generateSlotsForCollege(userId, college.id);
      console.log(`✅ Created ${slots?.length || 0} essay slots for ${college.global_colleges?.name || college.name}`);
      console.log('Created slots:', slots);

      if (!slots || slots.length === 0) {
        console.warn('⚠️ No slots were created - this might indicate a problem');
      }
    } catch (slotError) {
      // Log the error but don't fail the whole operation - prompts are already saved
      console.error('❌ Error creating essay slots (prompts are saved):', slotError);
      console.error('Stack trace:', slotError.stack);
      toast.error('Essay prompts saved but slot creation failed. Check console for details.');
    }

    toast.success(`Generated essay requirements for ${college.global_colleges?.name || college.name}`);

    if (onSuccess) onSuccess(result);
    return result;
  } catch (error) {
    console.error('Error generating essay requirements:', error);
    toast.error('Failed to generate essay requirements: ' + error.message);
    throw error;
  }
};

/**
 * Generate campus culture data
 * @param {Object} college - College object
 * @param {Function} onSuccess - Callback after successful generation
 */
export const generateCultureData = async (college, onSuccess) => {
  const collegeId = college.id || college.global_college_id;

  // Check if already generating
  if (isGenerating(collegeId, 'culture')) {
    console.log(`⏭️ Skipping ${college.name} - already generating culture data`);
    return;
  }

  startGeneration(collegeId, 'culture');

  try {
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-college-data',
        collegeName: college.global_colleges?.name || college.name,
        dataFields: 'culture'
      })
    });

    if (!response.ok) throw new Error('Failed to fetch culture data');

    const result = await response.json();
    console.log('Culture data API response:', result);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'No culture data returned from AI service');
    }

    // Flatten the nested AI response
    const flattenedData = flattenCollegeData(result.data, { verbose: true });
    console.log('Flattened culture data:', flattenedData);

    // Get the global college ID
    const globalCollegeId = college.global_college_id || college.global_colleges?.id;

    if (!globalCollegeId) {
      throw new Error('No global college ID found');
    }

    // Save to global_colleges table
    const { error: updateError } = await supabase
      .from('global_colleges')
      .update({
        ...flattenedData,
        data_last_updated: new Date().toISOString()
      })
      .eq('id', globalCollegeId);

    if (updateError) {
      console.error('Database update error:', updateError);
      throw new Error('Failed to save culture data: ' + updateError.message);
    }

    toast.success(`Generated culture data for ${college.global_colleges?.name || college.name}`);

    if (onSuccess) onSuccess(result);
    return result;
  } catch (error) {
    console.error('Error generating culture data:', error);
    toast.error('Failed to generate culture data');
    throw error;
  } finally {
    endGeneration(collegeId, 'culture');
  }
};

/**
 * Generate financial data
 * @param {Object} college - College object
 * @param {Function} onSuccess - Callback after successful generation
 */
export const generateFinancialData = async (college, onSuccess) => {
  const collegeId = college.id || college.global_college_id;

  // Check if already generating
  if (isGenerating(collegeId, 'financial')) {
    console.log(`⏭️ Skipping ${college.name} - already generating financial data`);
    return;
  }

  startGeneration(collegeId, 'financial');

  try {
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'fetch-college-data',
        collegeName: college.global_colleges?.name || college.name,
        dataFields: 'financial'
      })
    });

    if (!response.ok) throw new Error('Failed to fetch financial data');

    const result = await response.json();
    console.log('Financial data API response:', result);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'No financial data returned from AI service');
    }

    // Flatten the nested AI response
    const flattenedData = flattenCollegeData(result.data, { verbose: true });
    console.log('Flattened financial data:', flattenedData);

    // Get the global college ID
    const globalCollegeId = college.global_college_id || college.global_colleges?.id;

    if (!globalCollegeId) {
      throw new Error('No global college ID found');
    }

    // Save to global_colleges table
    const { error: updateError } = await supabase
      .from('global_colleges')
      .update({
        ...flattenedData,
        data_last_updated: new Date().toISOString()
      })
      .eq('id', globalCollegeId);

    if (updateError) {
      console.error('Database update error:', updateError);
      throw new Error('Failed to save financial data: ' + updateError.message);
    }

    toast.success(`Generated financial data for ${college.global_colleges?.name || college.name}`);

    if (onSuccess) onSuccess(result);
    return result;
  } catch (error) {
    console.error('Error generating financial data:', error);
    toast.error('Failed to generate financial data');
    throw error;
  } finally {
    endGeneration(collegeId, 'financial');
  }
};

/**
 * Generate honors program information
 * @param {Object} college - College object
 * @param {Object} studentProfile - Student profile data
 * @param {Function} onSuccess - Callback after successful generation
 */
export const generateHonorsPrograms = async (college, studentProfile, onSuccess) => {
  try {
    const response = await fetch('/api/anthropic/suggestions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'identify-honors-programs',
        collegeList: [college.global_colleges?.name || college.name],
        studentProfile: studentProfile
      })
    });

    if (!response.ok) throw new Error('Failed to fetch honors programs');

    const data = await response.json();
    toast.success(`Generated honors program data for ${college.global_colleges?.name || college.name}`);

    if (onSuccess) onSuccess(data);
    return data;
  } catch (error) {
    console.error('Error generating honors programs:', error);
    toast.error('Failed to generate honors program data');
    throw error;
  }
};

/**
 * Generate all missing data for a college
 * @param {Object} college - College object
 * @param {string} userId - Current user ID
 * @param {Object} studentProfile - Student profile data
 * @param {Function} onProgress - Callback for progress updates
 * @param {Function} onComplete - Callback after all generation complete
 */
export const generateAllMissingData = async (college, userId, studentProfile, onProgress, onComplete) => {
  const missing = getMissingData(college);
  const total = missing.length;
  let completed = 0;

  if (total === 0) {
    toast.success('All data already exists!');
    if (onComplete) onComplete();
    return;
  }

  toast.info(`Generating ${total} missing data types...`);

  try {
    for (const dataType of missing) {
      if (onProgress) onProgress({ current: completed, total, dataType });

      switch (dataType) {
        case 'basic_data':
          await generateBasicData(college);
          break;
        case 'deadlines':
          await generateDeadlines(college);
          break;
        case 'essays':
          await generateEssayRequirements(college, userId);
          break;
        case 'culture':
          await generateCultureData(college);
          break;
        case 'financial':
          await generateFinancialData(college);
          break;
        case 'honors':
          await generateHonorsPrograms(college, studentProfile);
          break;
      }

      completed++;
    }

    toast.success(`Successfully generated all missing data for ${college.global_colleges?.name || college.name}!`);
    if (onComplete) onComplete();
  } catch (error) {
    console.error('Error in batch generation:', error);
    toast.error(`Generated ${completed} of ${total} data types before error`);
    if (onComplete) onComplete();
  }
};
